import {
    AiGeneratedLabel,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/ui/design-system/components';
import FlavorBadgesList from './FlavorBadgesList';
import { SoftButton } from './Button';
import { cn } from '@/ui/design-system/utils/cn';
import { mixpanelTrack } from 'utils/app';
import { useCategoryDescriptions } from 'utils/hooks';
import { useFlagActions } from 'utils/hooks/flags';
import { useMutation } from 'react-query';

function AiFeedbackContent({ categoryId, categoryName, description }) {
    const { submitDescriptionFeedback } = useFlagActions();
    const { mutateAsync: sendFeedback, status } = useMutation((data) =>
        submitDescriptionFeedback(data),
    );
    const onClick = () =>
        sendFeedback({
            suggestion: 'GPT Description',
            comment: `Issue with "${categoryName}" (${categoryId}) GPT Description`,
            categoryId,
            categoryName,
            description,
        });

    return (
        <div className="flex flex-col items-start gap-2">
            <p className="text-muted-foreground">
                This description was generated by AI.
            </p>
            <SoftButton
                intent="secondary"
                size="sm"
                disabled={status === 'loading'}
                fetching={status === 'loading'}
                onClick={onClick}
                mixpanelMeta={{ categoryName, analysisType: 'gpt-description' }}
            >
                Send Feedback
            </SoftButton>
        </div>
    );
}

function AiDescriptionLabel({ tone, ...props }) {
    return (
        <Popover
            onOpenChange={(open) =>
                open &&
                mixpanelTrack('Click', {
                    contentKey: 'aiDescription',
                    context: 'Tooltip',
                })
            }
        >
            <PopoverTrigger className="group self-start rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring">
                <AiGeneratedLabel
                    tone={tone}
                    className="group-hover:text-foreground"
                />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                aria-label="AI-generated description"
                className="w-auto max-w-xs p-3 font-sans text-sm"
            >
                <AiFeedbackContent {...props} />
            </PopoverContent>
        </Popover>
    );
}

const containerClassName = (plain) =>
    cn(
        'max-w-[37.5rem] font-sans text-sm font-normal',
        !plain && 'rounded-lg bg-muted px-4 py-3',
    );

function CategoryDescription({ categoryId, categoryName, flavors, plain }) {
    const categoryDescriptionsQuery = useCategoryDescriptions({
        categories: categoryId ? [categoryId] : null,
    });
    const categoryDescription = categoryDescriptionsQuery.data?.find(
        ({ category }) => category === categoryId,
    );
    const { description, is_human_description } = categoryDescription || {};

    if (
        categoryDescriptionsQuery.isLoading ||
        categoryDescriptionsQuery.status === 'idle'
    )
        return (
            <div className={cn(containerClassName(plain), 'min-h-8 w-full')} />
        );

    if (categoryDescriptionsQuery.isError)
        return (
            <div className={cn(containerClassName(plain), 'text-destructive')}>
                There was an error loading the category description.
            </div>
        );

    if (!(description && description.length)) return null;

    return (
        <div className={containerClassName(plain)}>
            <div className="flex min-w-0 flex-col gap-1">
                {!is_human_description && (
                    <AiDescriptionLabel
                        tone={plain ? 'default' : 'strong'}
                        categoryId={categoryId}
                        categoryName={categoryName}
                        description={description}
                    />
                )}
                <span className="text-foreground">{description}</span>
            </div>
            <FlavorBadgesList categoryId={categoryId} flavors={flavors} />
        </div>
    );
}

export default CategoryDescription;
